import { useEffect, useState } from 'react';

let scriptPromise = null;

export function getGoogleMapsApiKey() {
    return process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
}

export function useGoogleMapsScript() {
    const apiKey = getGoogleMapsApiKey();
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!apiKey) {
            setError('missing_key');
            setReady(false);
            return undefined;
        }
        if (window.google?.maps?.places) {
            setReady(true);
            setError(null);
            return undefined;
        }
        if (!scriptPromise) {
            scriptPromise = new Promise((resolve, reject) => {
                const existing = document.querySelector('script[data-google-maps="true"]');
                if (existing) {
                    existing.addEventListener('load', () => resolve());
                    existing.addEventListener('error', reject);
                    return;
                }
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=it&region=IT`;
                script.async = true;
                script.defer = true;
                script.dataset.googleMaps = 'true';
                script.onload = () => resolve();
                script.onerror = () => reject(new Error('Google Maps script failed'));
                document.head.appendChild(script);
            });
        }
        let cancelled = false;
        scriptPromise
            .then(() => {
                if (!cancelled) {
                    setReady(true);
                    setError(null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setError('load_failed');
                    setReady(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [apiKey]);

    return { ready, error, apiKey };
}

function getComponent(components, type, useShort = false) {
    const item = components.find((c) => c.types.includes(type));
    if (!item) return '';
    return useShort ? (item.short_name || item.long_name || '') : (item.long_name || '');
}

export function parseGooglePlace(place) {
    const components = place?.address_components || [];
    const streetNumber = getComponent(components, 'street_number');
    const route = getComponent(components, 'route');
    const street = [route, streetNumber].filter(Boolean).join(' ').trim();
    const city = getComponent(components, 'locality')
        || getComponent(components, 'administrative_area_level_3')
        || getComponent(components, 'postal_town');
    const province = getComponent(components, 'administrative_area_level_2', true)
        || getComponent(components, 'administrative_area_level_1', true);
    const zip = getComponent(components, 'postal_code');
    const country = getComponent(components, 'country', true);

    return {
        street: street || (place?.formatted_address || '').split(',')[0]?.trim() || '',
        city,
        province: province.toUpperCase().slice(0, 2),
        zip,
        country: country.toUpperCase().slice(0, 2),
        google_place_id: place?.place_id || '',
    };
}
